import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PiBookOpenText, PiCaretDown, PiTrash } from "react-icons/pi";
import {
  DictionaryFilter,
  DictionarySort,
  DictionaryWord,
  useMyDictionaryQuery,
  useRemoveWordMutation,
} from "./queries";
import { lstr } from "./localization";
import { GENDER_BY_NAME, GENDER_CLASS, supportsGenderColoring } from "./gender";
import { StrengthDot, StrengthLegend } from "./StrengthDot";
import { SkeletonBlock } from "./Skeleton";
import { Pup } from "./Pup";
import { useSearchParamsState } from "./searchParamsState";

function PartOfSpeechBadge({ partOfSpeech }: { partOfSpeech: string }) {
  return (
    <span className="rounded-full bg-cream-dark px-2 py-0.5 text-[11px] font-medium text-secondary-text">
      {partOfSpeech}
    </span>
  );
}

// DeleteWordControl is a two-step delete: the first click reveals a
// confirm/cancel pair so a stray tap can't remove a word. Removal invalidates
// the My Dictionary cache, so the card disappears on success.
function DeleteWordControl({ word, l }: { word: DictionaryWord; l: string }) {
  const [confirming, setConfirming] = useState(false);
  const removeMutation = useRemoveWordMutation();

  if (confirming) {
    return (
      <div className="flex items-center gap-2">
        {removeMutation.isError && (
          <span className="text-sm text-error">
            {lstr(l).my_dictionary_delete_error}
          </span>
        )}
        <button
          type="button"
          disabled={removeMutation.isPending}
          onClick={() =>
            removeMutation.mutate({ dictionaryEntryId: word.dictionaryEntryId })
          }
          className="rounded-full px-2.5 py-1 text-sm font-semibold text-error transition-colors hover:bg-error/10 disabled:opacity-50"
        >
          {removeMutation.isPending
            ? lstr(l).my_dictionary_deleting
            : lstr(l).my_dictionary_delete_confirm}
        </button>
        <button
          type="button"
          disabled={removeMutation.isPending}
          onClick={() => setConfirming(false)}
          className="rounded-full px-2.5 py-1 text-sm font-medium text-secondary-text transition-colors hover:bg-cream-dark hover:text-main-text disabled:opacity-50"
        >
          {lstr(l).my_dictionary_delete_cancel}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      aria-label={lstr(l).my_dictionary_delete}
      title={lstr(l).my_dictionary_delete}
      className="flex h-8 w-8 items-center justify-center rounded-full text-secondary-text/70 transition-colors hover:bg-cream-dark hover:text-error"
    >
      <PiTrash size={16} />
    </button>
  );
}

function WordCard({
  word,
  l,
  r,
}: {
  word: DictionaryWord;
  l: string;
  r: string;
}) {
  const gender = GENDER_BY_NAME[word.gender];
  const genderClass =
    gender && supportsGenderColoring(r)
      ? GENDER_CLASS[gender]
      : "text-main-text";
  return (
    <div className="flex items-start gap-3 px-4 py-3.5 sm:px-5">
      <span className="mt-2 shrink-0">
        <StrengthDot
          l={l}
          practiced={word.practiced}
          recognizeStrength={word.recognizeStrength}
          produceStrength={word.produceStrength}
        />
      </span>
      <div className="min-w-0 flex-grow">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span
            lang={r}
            className={`font-literata text-[18px] font-semibold ${genderClass}`}
          >
            {word.displayForm}
          </span>
          {word.partOfSpeech !== "interjection" &&
            word.partOfSpeech !== "other" && (
              <PartOfSpeechBadge partOfSpeech={word.partOfSpeech} />
            )}
          {word.leech && (
            <span className="rounded-full bg-highlight-light px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
              {lstr(l).my_dictionary_tricky}
            </span>
          )}
        </div>
        {word.briefMeaning ? (
          <div lang={l} className="mt-0.5 text-[15px] text-main-text">
            {word.briefMeaning}
          </div>
        ) : (
          <div className="mt-0.5 text-sm italic text-secondary-text">
            {lstr(l).my_dictionary_meaning_pending}
          </div>
        )}
        {word.examples.length > 0 && (
          <ul
            aria-label={lstr(l).my_dictionary_examples_heading}
            className="mt-2 flex flex-col gap-1 border-l-2 border-primary/20 pl-3"
          >
            {word.examples.map((example, idx) => (
              <li
                key={idx}
                lang={r}
                className="font-literata text-[14.5px] italic leading-snug text-secondary-text"
              >
                {example}
              </li>
            ))}
          </ul>
        )}
      </div>
      {word.saved && (
        <span className="-mr-1 shrink-0">
          <DeleteWordControl word={word} l={l} />
        </span>
      )}
    </div>
  );
}

const SORTS: DictionarySort[] = ["recent", "weakest", "strongest"];

// The page index (0-based, as the API's) from the URL; anything but a
// non-negative integer means the first page.
function parsePage(value: string | null): number {
  const page = value !== null && /^\d+$/.test(value) ? Number(value) : 0;
  return Number.isSafeInteger(page) ? page : 0;
}

export function MyDictionaryView({ l, r }: { l: string; r: string }) {
  const strings = lstr(l);
  // Filter, sort and page live in the URL; practice links to all words.
  const [searchParams, updateSearchParams] = useSearchParamsState();
  const filter: DictionaryFilter =
    searchParams.get("filter") === "all" ? "all" : "saved";
  const sortParam = searchParams.get("sort") as DictionarySort | null;
  const sort: DictionarySort =
    sortParam !== null && SORTS.includes(sortParam) ? sortParam : "recent";
  const page = parsePage(searchParams.get("page"));
  const query = useMyDictionaryQuery(l, r, page, filter, sort);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // A page number from one filter, sort or language pair is meaningless in
  // another, so changing any of them starts from the first page.
  const setFilter = (value: DictionaryFilter) =>
    updateSearchParams({ filter: value === "all" ? "all" : null, page: null });
  const setSort = (value: DictionarySort) =>
    updateSearchParams({ sort: value === "recent" ? null : value, page: null });
  const setPage = useCallback(
    (value: number) =>
      updateSearchParams({ page: value > 0 ? value.toString() : null }),
    [updateSearchParams],
  );
  const languagePair = `${l}:${r}`;
  const languagePairRef = useRef(languagePair);
  useEffect(() => {
    if (languagePairRef.current === languagePair) return;
    languagePairRef.current = languagePair;
    setPage(0);
  }, [languagePair, setPage]);

  // A new page is read from the top. Focus moves along too: the pagination
  // button that had it is disabled while the page loads.
  const goToPage = (value: number) => {
    setPage(value);
    headingRef.current?.scrollIntoView({ block: "start" });
    headingRef.current?.focus({ preventScroll: true });
  };
  const sortLabels: Record<DictionarySort, string> = {
    recent: strings.my_dictionary_sort_recent,
    weakest: strings.my_dictionary_sort_weakest,
    strongest: strings.my_dictionary_sort_strongest,
  };

  // Deleting the last word of a trailing page leaves it empty; step back so
  // the user isn't stranded on a blank page. A page that never showed words
  // (a stale link past the end) goes back to the last page that did, or the
  // first, rather than walking back one request at a time.
  const lastPageWithWordsRef = useRef<number | null>(null);
  const pageShowsWords =
    query.isSuccess && !query.isPlaceholderData && query.data.words.length > 0;
  useEffect(() => {
    if (pageShowsWords) lastPageWithWordsRef.current = page;
  }, [pageShowsWords, page]);
  const isOnEmptyTrailingPage =
    query.isSuccess &&
    !query.isFetching &&
    query.data.words.length === 0 &&
    page > 0;
  useEffect(() => {
    if (!isOnEmptyTrailingPage) return;
    const lastPageWithWords = lastPageWithWordsRef.current;
    setPage(
      lastPageWithWords === null ? 0 : Math.min(lastPageWithWords, page - 1),
    );
  }, [isOnEmptyTrailingPage, page, setPage]);

  return (
    <div className="w-full pb-10">
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="scroll-mt-20 font-literata text-[28px] font-bold leading-tight text-main-text outline-none"
      >
        {lstr(l).my_dictionary_header}
      </h1>

      <div className="mb-3 mt-4 flex flex-wrap items-center justify-between gap-3">
        <div
          role="tablist"
          className="grid grid-cols-2 gap-1 rounded-full bg-cream-dark p-1"
        >
          {(["saved", "all"] as DictionaryFilter[]).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={filter === value}
              onClick={() => setFilter(value)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                filter === value
                  ? "bg-surface text-main-text shadow-card"
                  : "text-secondary-text hover:text-main-text"
              }`}
            >
              {value === "saved"
                ? strings.my_dictionary_filter_saved
                : strings.my_dictionary_filter_all}
            </button>
          ))}
        </div>
        <span className="relative inline-flex">
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value as DictionarySort)}
            aria-label={sortLabels[sort]}
            className="h-9 cursor-pointer appearance-none rounded-full border border-border bg-surface pl-4 pr-9 text-sm font-medium text-main-text focus:border-primary/60 focus:outline-none focus:ring-4 focus:ring-primary/15"
          >
            {SORTS.map((value) => (
              <option key={value} value={value}>
                {sortLabels[value]}
              </option>
            ))}
          </select>
          <PiCaretDown
            size={13}
            aria-hidden="true"
            className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-secondary-text"
          />
        </span>
      </div>
      <StrengthLegend l={l} />

      {query.isPending && (
        <div
          className="card mt-3 divide-y divide-border overflow-hidden"
          aria-busy="true"
        >
          <span className="sr-only">{lstr(l).my_dictionary_loading}</span>
          {Array.from({ length: 6 }, (_, idx) => (
            <div key={idx} className="flex items-start gap-3 px-5 py-4">
              <SkeletonBlock className="mt-1 h-3 w-3 rounded-full" />
              <div className="flex flex-grow flex-col gap-2">
                <SkeletonBlock className="h-4 w-32" />
                <SkeletonBlock className="h-3.5 w-48" />
              </div>
            </div>
          ))}
        </div>
      )}

      {query.isError && (
        <div className="card mt-3 px-6 py-8 text-center text-error">
          {lstr(l).my_dictionary_error}
        </div>
      )}

      {query.isSuccess && query.data.words.length === 0 && page === 0 && (
        <div className="card mt-3 flex flex-col items-center gap-3 px-6 py-10 text-center">
          <Pup shape="circle" className="h-20 w-20" />
          <p className="max-w-sm text-secondary-text">
            {filter === "all"
              ? strings.my_dictionary_all_empty
              : strings.my_dictionary_empty}
          </p>
          <Link to="/" className="btn-primary mt-2 px-5 py-2.5">
            <PiBookOpenText size={18} />
            {strings.my_dictionary_empty_cta}
          </Link>
        </div>
      )}

      {query.isSuccess && query.data.words.length > 0 && (
        // While the next page loads, the previous one stays, dimmed.
        <div
          aria-busy={query.isPlaceholderData}
          className={`card mt-3 divide-y divide-border overflow-hidden transition-opacity ${
            query.isPlaceholderData ? "opacity-60" : ""
          }`}
        >
          {query.data.words.map((word) => (
            <WordCard key={word.dictionaryEntryId} word={word} l={l} r={r} />
          ))}
        </div>
      )}

      {query.isSuccess && (page > 0 || query.data.hasMore) && (
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            disabled={page === 0 || query.isFetching}
            onClick={() => goToPage(page - 1)}
            className="btn-secondary px-4 py-2 text-sm"
          >
            {lstr(l).my_dictionary_prev_page}
          </button>
          <span className="text-sm text-secondary-text">
            {lstr(l).my_dictionary_page_label} {page + 1}
          </span>
          <button
            type="button"
            disabled={!query.data.hasMore || query.isFetching}
            onClick={() => goToPage(page + 1)}
            className="btn-secondary px-4 py-2 text-sm"
          >
            {lstr(l).my_dictionary_next_page}
          </button>
        </div>
      )}
    </div>
  );
}
